import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  FilterAlt,
  Refresh,
  Timeline,
} from '@mui/icons-material';
import { eventsAPI } from '../../api/events';
import apiClient from '../../api/apiClient';
import { INDICATOR_EVENT_DEFINITIONS, getEventTypeLabel } from '../../constants/eventTypes';
import { TableSkeleton } from '../../components/LoadingSpinner';
import DWLFLoader from '../../components/DWLFLoader';
import PageHeader from '../../components/PageHeader';
import { describeKeyLevelEvent, isKeyLevelEvent } from '../../utils/eventHelpers';

const ALL_EVENT_TYPES = INDICATOR_EVENT_DEFINITIONS.map((definition) => definition.id);

const TIMEFRAME_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: 'daily', label: '1D' },
  { value: 'weekly', label: '1W' },
];

const EVENT_SOURCE_OPTIONS = [
  { value: 'system', label: 'System events' },
  { value: 'custom', label: 'My custom events' },
];

const DEFAULT_FILTERS = {
  eventSource: 'system',
  eventType: '',
  eventTypes: ALL_EVENT_TYPES,
  symbols: [],
  timeframe: '',
  days: 14,
  sortOrder: 'desc',
  limit: 25,
  uniquePerSymbol: false,
  fromDate: '',
  toDate: '',
};

const sortEvents = (events, sortField, sortOrder) => {
  const sorted = [...events];
  const order = sortOrder === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];

    if (sortField === 'eventTimestamp' || sortField === 'date') {
      return order * (new Date(aValue || 0) - new Date(bValue || 0));
    }

    return order * String(aValue || '').localeCompare(String(bValue || ''));
  });

  return sorted;
};

const describeEvent = (event) => {
  if (!event) {
    return '';
  }

  if (isKeyLevelEvent(event.eventType)) {
    return describeKeyLevelEvent(event);
  }
  switch (event.eventType) {
    case 'trendline_break':
    case 'trendline_break_bullish':
    case 'trendline_break_bearish':
      return `Trendline break near ${event.level || event.breakPrice || 'unknown level'}`;
    case 'trendline_breach':
    case 'trendline_breach_bullish':
    case 'trendline_breach_bearish':
      return `Trendline breach near ${event.level || event.breakPrice || 'unknown level'}`;
    case 'swing_low_formed':
      return `Swing low at ${event.level || 'unknown level'}`;
    case 'swing_high_formed':
      return `Swing high at ${event.level || 'unknown level'}`;
    case 'swing_low_break':
      return `Swing low break at ${event.breakPrice || event.level || 'unknown level'}`;
    case 'swing_high_break':
      return `Swing high break at ${event.breakPrice || event.level || 'unknown level'}`;
    case 'swing_low_sweep':
      return `Swing low sweep at ${event.sweepPrice || event.level || 'unknown level'}`;
    case 'swing_high_sweep':
      return `Swing high sweep at ${event.sweepPrice || event.level || 'unknown level'}`;
    case 'lower_low':
      return `Lower low at ${event.level || 'unknown level'}`;
    case 'lower_high':
      return `Lower high at ${event.level || 'unknown level'}`;
    case 'higher_low':
      return `Higher low at ${event.level || 'unknown level'}`;
    case 'higher_high':
      return `Higher high at ${event.level || 'unknown level'}`;
    case 'cycle.low.higher_low':
      return `Higher cycle low at ${event.pivotPrice || event.level || 'unknown level'}`;
    case 'cycle.low.lower_low':
      return `Lower cycle low at ${event.pivotPrice || event.level || 'unknown level'}`;
    case 'cycle.high.higher_high':
      return `Higher cycle high at ${event.pivotPrice || event.level || 'unknown level'}`;
    case 'cycle.high.lower_high':
      return `Lower cycle high at ${event.pivotPrice || event.level || 'unknown level'}`;
    case 'cycle.low.sweep':
      return `Cycle low sweep at ${event.sweepPrice || event.pivotPrice || event.level || 'unknown level'}`;
    case 'cycle.high.sweep':
      return `Cycle high sweep at ${event.sweepPrice || event.pivotPrice || event.level || 'unknown level'}`;
    case 'custom_event':
      return event.eventName || 'Custom event';
    default:
      return getEventTypeLabel(event.eventType);
  }
};

const getEventChipLabel = (event) => {
  if (!event) {
    return '';
  }
  if (event.eventType === 'custom_event') {
    return event.eventName || 'Custom Event';
  }
  return getEventTypeLabel(event.eventType);
};

const directionChip = (event) => {
  const breakoutDirection = String(event.breakoutDirection || '').toLowerCase();
  const movementDirection = String(event.movementDirection || '').toLowerCase();
  const eventType = String(event.eventType || '').toLowerCase();
  const eventName = String(event.eventName || '').toLowerCase();

  // "Unalignment" events (e.g. ema_bullish_unalignment) represent the
  // END of a directional signal, not the signal itself — they should
  // stay Neutral rather than inheriting the direction keyword.
  const isUnalignment = eventType.includes('unalignment');

  const isBullish = !isUnalignment && (
    breakoutDirection === 'up' ||
    breakoutDirection === 'bullish' ||
    movementDirection === 'approaching_from_below' ||
    eventType.includes('bullish') ||
    eventName.includes('bullish') ||
    eventType.includes('higher_high') ||
    eventType.includes('higher_low') ||
    eventType.includes('swing_low_formed')
  );

  const isBearish = !isUnalignment && (
    breakoutDirection === 'down' ||
    breakoutDirection === 'bearish' ||
    movementDirection === 'approaching_from_above' ||
    eventType.includes('bearish') ||
    eventName.includes('bearish') ||
    eventType.includes('lower_low') ||
    eventType.includes('lower_high') ||
    eventType.includes('swing_high_formed')
  );

  if (isBullish) {
    return <Chip label="Bullish" color="success" size="small" />;
  }
  if (isBearish) {
    return <Chip label="Bearish" color="error" size="small" />;
  }
  return <Chip label="Neutral" color="info" size="small" />;
};

const formatDateTime = (timestamp, fallbackDate) => {
  const dateValue = timestamp || fallbackDate;
  if (!dateValue) {
    return 'N/A';
  }
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return fallbackDate || 'N/A';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const EventsPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [formValues, setFormValues] = useState(DEFAULT_FILTERS);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [pageCursors, setPageCursors] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [countTruncated, setCountTruncated] = useState(false);
  const [sortField, setSortField] = useState('eventTimestamp');

  useEffect(() => {
    const pageCursor = pageCursors[currentPage] || null;

    const fetchEvents = async () => {
      setLoading(true);
      setError('');
      setInfo('');

      try {
        const isCustomSource = filters.eventSource === 'custom';
        const eventTypesForQuery = isCustomSource ? ['custom_event'] : filters.eventTypes;
        const response = await eventsAPI.getRecentEvents({
          type: eventTypesForQuery?.length === 1 ? eventTypesForQuery[0] : undefined,
          eventTypes: eventTypesForQuery && eventTypesForQuery.length > 0 ? eventTypesForQuery : undefined,
          scope: isCustomSource ? 'user' : undefined,
          days: filters.days,
          symbols: filters.symbols && filters.symbols.length ? filters.symbols : undefined,
          timeframe: filters.timeframe || undefined,
          sortOrder: filters.sortOrder,
          limit: filters.limit,
          uniquePerSymbol: filters.uniquePerSymbol,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
          cursor: pageCursor || undefined,
          includeCount: true,
        });

        setEvents(response.events || []);
        setCursor(response.cursor || null);
        setTotalCount(
          typeof response.totalCount === 'number' ? response.totalCount : null
        );
        setCountTruncated(Boolean(response.countTruncated));
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to fetch events';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [filters, currentPage, pageCursors]);

  useEffect(() => {
    const fetchSymbols = async () => {
      setSymbolsLoading(true);
      try {
        const symbolsResponse = await apiClient.get('/symbols');
        const symbols = Array.isArray(symbolsResponse.data?.symbols) ? symbolsResponse.data.symbols : [];
        const activeSymbols = symbols
          .filter((item) => item?.isActive === true || item?.isActive === 'true' || item?.isActive === 1 || item?.isActive === '1')
          .map((item) => item.symbol)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setSymbolOptions(activeSymbols);
      } catch (err) {
        // Non-fatal: leave options empty
        console.warn('Failed to load symbols for events filter', err);
      } finally {
        setSymbolsLoading(false);
      }
    };
    fetchSymbols();
  }, []);

  const handleFormChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value =
      field === 'days' || field === 'limit'
        ? Number(rawValue)
        : rawValue;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEventSourceChange = (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      eventSource: value,
      eventTypes: value === 'custom' ? ['custom_event'] : ALL_EVENT_TYPES,
      eventType: '',
    }));
  };

  const handleSymbolsChange = (_, newValue) => {
    setFormValues((prev) => ({
      ...prev,
      symbols: newValue,
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSelectAllEvents = () => {
    setFormValues((prev) => ({
      ...prev,
      eventTypes: ALL_EVENT_TYPES,
      eventType: '',
    }));
  };

  const handleClearEvents = () => {
    setFormValues((prev) => ({
      ...prev,
      eventTypes: [],
      eventType: '',
    }));
  };

  const handleApplyFilters = () => {
    setFilters(formValues);
    setPageCursors([null]);
    setCurrentPage(0);
    setInfo('');
    setTotalCount(null);
  };

  const handleResetFilters = () => {
    setFormValues(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPageCursors([null]);
    setCurrentPage(0);
    setInfo('');
    setTotalCount(null);
  };

  const handleNextPage = () => {
    const totalPages = totalCount ? Math.ceil(totalCount / filters.limit) : null;
    const isLastPage = totalPages ? currentPage + 1 >= totalPages : !cursor;

    if (isLastPage) {
      setInfo('No more pages available for these filters.');
      return;
    }
    setPageCursors((prev) => {
      const updated = prev.slice(0, currentPage + 1);
      updated[currentPage + 1] = cursor;
      return updated;
    });
    setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage === 0) return;
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const sortedEvents = useMemo(
    () => sortEvents(events, sortField, filters.sortOrder),
    [events, sortField, filters.sortOrder]
  );

  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / filters.limit)) : null;
  const isLastPage = totalPages ? currentPage + 1 >= totalPages : !cursor;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Events"
        subtitle="Explore detection events across symbols and timeframes"
        icon={<Timeline />}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAlt sx={{ color: 'var(--dwlf-accent)' }} />
              <Typography 
                sx={{ 
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                Filters
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters} 
                startIcon={<Refresh />}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  borderColor: 'var(--dwlf-ink-20)',
                  color: 'var(--dwlf-ink-60)',
                  '&:hover': {
                    borderColor: 'var(--dwlf-accent)',
                    color: 'var(--dwlf-accent)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Reset
              </Button>
              <Button 
                variant="contained" 
                onClick={handleApplyFilters} 
                startIcon={<FilterAlt />}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  backgroundColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-bg)',
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-text)',
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="event-source-label">Event source</InputLabel>
                <Select
                  labelId="event-source-label"
                  value={formValues.eventSource}
                  label="Event source"
                  onChange={handleEventSourceChange}
                >
                  {EVENT_SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={1}>
                <FormControl fullWidth>
                  <InputLabel id="event-type-label">Event Types</InputLabel>
                  <Select
                    labelId="event-type-label"
                    multiple
                    value={formValues.eventTypes}
                    label="Event Types"
                    onChange={handleFormChange('eventTypes')}
                    disabled={formValues.eventSource === 'custom'}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return '';
                      }
                      if (selected.length === 1) {
                        const single = INDICATOR_EVENT_DEFINITIONS.find(
                          (definition) => definition.id === selected[0]
                        );
                        return single ? single.name : getEventTypeLabel(selected[0]);
                      }
                      return `${selected.length} selected`;
                    }}
                  >
                    {formValues.eventSource === 'custom' && (
                      <MenuItem value="custom_event">Custom Event</MenuItem>
                    )}
                    {INDICATOR_EVENT_DEFINITIONS.map((definition) => (
                      <MenuItem key={definition.id} value={definition.id}>
                        {definition.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleSelectAllEvents}
                    disabled={formValues.eventSource === 'custom'}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleClearEvents}
                    disabled={formValues.eventSource === 'custom'}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                options={symbolOptions}
                loading={symbolsLoading}
                value={formValues.symbols}
                onChange={handleSymbolsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Symbols (optional)"
                    placeholder="Type to search"
                  />
                )}
                disableCloseOnSelect
                clearOnBlur={false}
                blurOnSelect={false}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth sx={{ minWidth: 180 }}>
                <InputLabel id="timeframe-label">Timeframe</InputLabel>
                <Select
                  labelId="timeframe-label"
                  value={formValues.timeframe}
                  label="Timeframe"
                  onChange={handleFormChange('timeframe')}
                >
                  {TIMEFRAME_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'any'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Lookback (days)"
                type="number"
                value={formValues.days}
                onChange={handleFormChange('days')}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="From date"
                type="date"
                value={formValues.fromDate}
                onChange={handleFormChange('fromDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="To date"
                type="date"
                value={formValues.toDate}
                onChange={handleFormChange('toDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="sort-order-label">Sort Order</InputLabel>
                <Select
                  labelId="sort-order-label"
                  value={formValues.sortOrder}
                  label="Sort Order"
                  onChange={handleFormChange('sortOrder')}
                >
                  <MenuItem value="desc">Newest first</MenuItem>
                  <MenuItem value="asc">Oldest first</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="sort-field-label">Sort Field</InputLabel>
                <Select
                  labelId="sort-field-label"
                  value={sortField}
                  label="Sort Field"
                  onChange={(event) => setSortField(event.target.value)}
                >
                  <MenuItem value="eventTimestamp">Event time</MenuItem>
                  <MenuItem value="symbol">Symbol</MenuItem>
                  <MenuItem value="eventType">Event type</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="page-size-label">Page size</InputLabel>
                <Select
                  labelId="page-size-label"
                  value={formValues.limit}
                  label="Page size"
                  onChange={handleFormChange('limit')}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size} events
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.uniquePerSymbol}
                    onChange={handleSwitchChange('uniquePerSymbol')}
                  />
                }
                label="Unique per symbol"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!error && info && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalPages ? `Page ${currentPage + 1} of ${totalPages}` : `Page ${currentPage + 1}`}
          </Typography>
        </Stack>

        {loading ? (
          <TableSkeleton rows={8} columns={6} />
        ) : (
          <>
            {sortedEvents.length === 0 ? (
              <Alert severity="info">No events found for the selected filters.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Timeframe</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Direction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEvents.map((event) => (
                    <TableRow key={`${event.symbol}-${event.eventType}-${event.eventTimestamp || event.date}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(event.date, event.eventTimestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={event.symbol} color="primary" variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip
                            label={getEventChipLabel(event)}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                          {event.confidenceScore !== undefined && event.confidenceScore !== null && (
                            <Typography variant="caption" color="text.secondary">
                              Confidence: {(event.confidenceScore * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{event.timeframe?.toUpperCase?.() || '—'}</TableCell>
                      <TableCell>
                        {(() => {
                          const description = describeEvent(event);
                          const chipLabel = getEventChipLabel(event);
                          // Don't repeat the same text that's already in the Event chip
                          if (!description || description.toLowerCase() === chipLabel.toLowerCase()) {
                            return <Typography variant="body2" color="text.secondary">—</Typography>;
                          }
                          return <Typography variant="body2">{description}</Typography>;
                        })()}
                      </TableCell>
                      <TableCell align="right">{directionChip(event)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Paper>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handlePrevPage}
                disabled={currentPage === 0 || loading}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNextPage}
                disabled={loading || isLastPage}
              >
                Next page
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {totalPages ? `Page ${currentPage + 1} of ${totalPages}` : `Page ${currentPage + 1}`}
              </Typography>
              <Divider flexItem orientation="vertical" />
              <Typography variant="body2" color="text.secondary">
                Showing {sortedEvents.length} events
              </Typography>
              {totalCount !== null && (
                <>
                  <Divider flexItem orientation="vertical" />
                  <Typography variant="body2" color="text.secondary">
                    Total {countTruncated ? '~' : ''}{totalCount} events
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EventsPage;
